import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation } from '@apollo/client'
import { gql } from '@apollo/client'
import { AnimatePresence, motion } from 'framer-motion'
import { isMobile } from 'react-device-detect'
import { ReactRef } from 'use-callback-ref/dist/es5/types'
import { CaptionSM } from '@/atoms/Text'
import { paths } from '@/constants'
import { Modal } from '@/molecules/Modal'
import { createBasicShareLink } from '@/services/Sharing'
import { logger } from '@/utils/logging'
import { useTranslate } from '@/utils/translate/translate-client'
import { CompletionStep } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/ShareGuildTicketsModal/CompletionStep'
import { ConfirmStep } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/ShareGuildTicketsModal/ConfirmationStep'
import { DefaultStep } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/ShareGuildTicketsModal/DefaultStep'
import { useGuildTickets } from '@/views/TicketCheckoutViews/hooks/useGuildTickets'

const DONATE_GUILD_TICKETS_MUTATION = gql`
  mutation DiscountsDonateGuildToPif($input: DiscountsDonateGuildToPifInput!) {
    discountsDonateGuildToPif(input: $input) {
      quantityGifted
      error {
        description
        reference
      }
    }
  }
`

interface ShareGuildTicketsModalProps {
  open: boolean
  onClose: () => void
  projectName: string
  theatricalSlug: string
  modalRef?: ReactRef<HTMLDivElement>
  ModalStep?: ModalStep
}

type ModalStep = 'main' | 'gift-confirmation' | 'gift-completion'

export const ShareGuildTicketsModal = ({
  open,
  onClose,
  projectName,
  theatricalSlug,
  modalRef,
  ModalStep = 'main',
}: ShareGuildTicketsModalProps) => {
  const { t } = useTranslate('common')
  const [isCopied, setIsCopied] = useState(false)
  const [shareUrl, setShareUrl] = useState<string | null>(null)
  const [currentStep, setCurrentStep] = useState<ModalStep>(ModalStep)
  const timeoutRef = useRef<NodeJS.Timeout>()
  const { discountCodes, giftGuildTicketsMobile } = useGuildTickets(theatricalSlug)

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        setIsCopied(false)
      }
    }
  }, [])

  useEffect(() => {
    if (!discountCodes || discountCodes.length === 0) {
      setShareUrl(null)
      return
    }

    createBasicShareLink({
      baseUrl: `${paths.getBaseURL()}/tickets/${theatricalSlug}`,
      params: { flow: 'guild-gift', discounts: discountCodes.join(',') },
    }).then((url: string) => setShareUrl(url))
  }, [discountCodes, theatricalSlug])

  const onCopyClick = useCallback(async () => {
    if (!shareUrl) return

    if (isMobile) {
      await giftGuildTicketsMobile()
      return
    }
    if (navigator?.clipboard) {
      navigator.clipboard.writeText(shareUrl).then(() => {
        setIsCopied(true)
      })

      const timeout = setTimeout(() => {
        setIsCopied(false)
      }, 2000)

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      timeoutRef.current = timeout
    }
  }, [shareUrl, giftGuildTicketsMobile])

  const handleGiftClick = useCallback(() => {
    setCurrentStep('gift-confirmation')
  }, [])

  const handleGoBack = useCallback(() => {
    setCurrentStep('main')
  }, [])

  const [donateGuildTickets, { loading: isDonating }] = useMutation(DONATE_GUILD_TICKETS_MUTATION)

  const handleGiftConfirm = useCallback(async () => {
    if (!discountCodes || discountCodes.length === 0) return

    try {
      const result = await donateGuildTickets({
        variables: {
          input: {
            quantity: discountCodes.length,
            theatricalSlug,
          },
        },
      })

      const { error } = result.data?.discountsDonateGuildToPif || {}

      if (error) {
        logger().error('Donating Guild Tickets Faile', {
          error,
          theatricalSlug,
        })
        return
      }
      setCurrentStep('gift-completion')
    } catch (err) {
      logger().error('Donating Guild Tickets Faile', {
        err,
        theatricalSlug,
      })
    }
  }, [discountCodes, donateGuildTickets, theatricalSlug])

  const renderStepContent = () => {
    const commonProps = {
      onClose,
      ticketQuantity: discountCodes.length,
      projectName,
    }
    switch (currentStep) {
      case 'gift-confirmation':
        return (
          <ConfirmStep
            {...commonProps}
            handleGiftConfirm={handleGiftConfirm}
            handleGoBack={handleGoBack}
            isLoading={isDonating}
          />
        )
      case 'gift-completion':
        return <CompletionStep {...commonProps} />
      default:
        return <DefaultStep onClose={onClose} onCopyClick={onCopyClick} handleGiftClick={handleGiftClick} />
    }
  }

  return (
    <Modal
      backgroundScreenColor="black"
      isOpen={open}
      onClose={onClose}
      backgroundOverlayClassName="!bg-black/85"
      panelClasses="relative shadow-dark-5 rounded-3xl  !w-[calc(100%-2em)] !max-w-[480px] !p-0 relative text-center"
      transition="appear"
      className="z-[99999]"
    >
      <div ref={modalRef} className="h-full w-full  min-w-[355px] !p-6 !pt-8 md:!p-10 md:!pt-12">
        {renderStepContent()}
        <AnimatePresence>
          {isCopied && (
            <motion.div
              className="absolute inset-x-0 bottom-3.5"
              transition={{
                y: { type: 'spring', bounce: 0.3 },
                opacity: { duration: 0.3 },
                ease: 'easeInOut',
              }}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
            >
              <CaptionSM color="core-gray-700">{t('urlCopiedToClipBoard', 'URL copied to clipboard')}</CaptionSM>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Modal>
  )
}
